import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  Workspace,
  Space,
  List,
  Task,
  TaskComment,
  TaskAttachment,
  TaskActivity,
  TaskAssignee,
  WorkspaceMember,
  WorkspaceInvite,
  CreateTaskRequest,
  UpdateTaskRequest,
  MoveTaskRequest,
  TaskQueryFilters,
  PaginatedTasksResponse,
  TasksByStatus,
  KanbanStatus,
} from "@/models/kanban";
import {
  getWorkspaces,
  getWorkspacesByUser,
  getWorkspaceById,
  getWorkspaceBySlug,
  createWorkspace,
  updateWorkspace,
  addWorkspaceMember,
  createWorkspaceInvite,
  getWorkspaceInvite,
  acceptWorkspaceInvite,
  declineWorkspaceInvite,
  removeWorkspaceMember,
  deleteWorkspace,
  getSpaces,
  getSpaceById,
  getSpaceBySlug,
  createSpace,
  updateSpace,
  deleteSpace,
  getLists,
  getListById,
  getListBySlug,
  createList,
  updateList,
  deleteList,
  getTasksByList,
  getTasksByStatus,
  getTaskById,
  getSubtasks,
  queryTasks,
  createTask,
  updateTask,
  moveTask,
  deleteTask,
  addTaskAssignee,
  removeTaskAssignee,
  getTaskComments,
  addTaskComment,
  updateTaskComment,
  deleteTaskComment,
  getTaskAttachments,
  addTaskAttachment,
  deleteTaskAttachment,
  getTaskActivity,
  reorderTasksInStatus,
  getOverdueTasks,
  getTasksDueToday,
  getMyTasks,
  getTasksByGoal,
} from "@/helpers/server-actions/kanban-collection";
import { Timestamp } from "firebase/firestore";

export const kanbanApi = createApi({
  reducerPath: "kanbanApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Workspace", "Space", "List", "Task", "Comment", "Attachment", "Activity", "Goal", "KeyResult"],
  endpoints: (build) => ({
    // ========================================================================
    // Workspace Endpoints
    // ========================================================================

    getWorkspaces: build.query<Workspace[], void>({
      async queryFn() {
        try {
          const workspaces = await getWorkspaces();
          return { data: workspaces };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: ["Workspace"],
    }),

    getWorkspacesByUser: build.query<Workspace[], string>({
      async queryFn(userId) {
        try {
          const workspaces = await getWorkspacesByUser(userId);
          return { data: workspaces };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: ["Workspace"],
    }),

    getWorkspaceById: build.query<Workspace | null, string>({
      async queryFn(workspaceId) {
        try {
          const workspace = await getWorkspaceById(workspaceId);
          return { data: workspace };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, id) => [{ type: "Workspace", id }],
    }),

    getWorkspaceBySlug: build.query<Workspace | null, string>({
      async queryFn(slug) {
        try {
          const workspace = await getWorkspaceBySlug(slug);
          return { data: workspace };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result) =>
        _result ? [{ type: "Workspace", id: _result.id }] : ["Workspace"],
    }),

    createWorkspace: build.mutation<
      Workspace,
      {
        data: {
          name: string;
          description?: string;
          type: "internal" | "partner";
          iconUrl?: string;
          color?: string;
        };
        creatorId: string;
        creatorEmail: string;
        creatorDisplayName: string;
      }
    >({
      async queryFn({ data, creatorId, creatorEmail, creatorDisplayName }) {
        try {
          const workspace = await createWorkspace(
            data,
            creatorId,
            creatorEmail,
            creatorDisplayName
          );
          return { data: workspace };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: ["Workspace"],
    }),

    updateWorkspace: build.mutation<
      Workspace | null,
      {
        workspaceId: string;
        data: Partial<Pick<Workspace, "name" | "description" | "iconUrl" | "color" | "settings">>;
      }
    >({
      async queryFn({ workspaceId, data }) {
        try {
          const workspace = await updateWorkspace(workspaceId, data);
          return { data: workspace };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Workspace", id: workspaceId },
      ],
    }),

    addWorkspaceMember: build.mutation<
      Workspace | null,
      { workspaceId: string; member: WorkspaceMember }
    >({
      async queryFn({ workspaceId, member }) {
        try {
          const workspace = await addWorkspaceMember(workspaceId, member);
          return { data: workspace };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Workspace", id: workspaceId },
      ],
    }),

    createWorkspaceInvite: build.mutation<
      string,
      {
        workspaceId: string;
        invite: Omit<WorkspaceInvite, "id" | "status" | "createdAt">;
      }
    >({
      async queryFn({ workspaceId, invite }) {
        try {
          const inviteId = await createWorkspaceInvite(workspaceId, invite);
          return { data: inviteId };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Workspace", id: workspaceId },
      ],
    }),

    getWorkspaceInvite: build.query<
      WorkspaceInvite | null,
      { workspaceId: string; inviteId: string }
    >({
      async queryFn({ workspaceId, inviteId }) {
        try {
          const invite = await getWorkspaceInvite(workspaceId, inviteId);
          return { data: invite };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    acceptWorkspaceInvite: build.mutation<
      void,
      { workspaceId: string; inviteId: string; user: { uid: string; email: string; displayName?: string } }
    >({
      async queryFn({ workspaceId, inviteId, user }) {
        try {
          await acceptWorkspaceInvite(workspaceId, inviteId, user);
          return { data: undefined };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    declineWorkspaceInvite: build.mutation<
      void,
      { workspaceId: string; inviteId: string; user: { uid: string; email: string; displayName?: string } }
    >({
      async queryFn({ workspaceId, inviteId, user }) {
        try {
          await declineWorkspaceInvite(workspaceId, inviteId, user);
          return { data: undefined };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    removeWorkspaceMember: build.mutation<
      Workspace | null,
      { workspaceId: string; userId: string }
    >({
      async queryFn({ workspaceId, userId }) {
        try {
          const workspace = await removeWorkspaceMember(workspaceId, userId);
          return { data: workspace };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Workspace", id: workspaceId },
      ],
    }),

    deleteWorkspace: build.mutation<boolean, string>({
      async queryFn(workspaceId) {
        try {
          const result = await deleteWorkspace(workspaceId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: ["Workspace", "Space", "List", "Task"],
    }),

    // ========================================================================
    // Space Endpoints
    // ========================================================================

    getSpaces: build.query<Space[], string>({
      async queryFn(workspaceId) {
        try {
          const spaces = await getSpaces(workspaceId);
          return { data: spaces };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, workspaceId) => [
        { type: "Space", id: workspaceId },
      ],
    }),

    getSpaceById: build.query<Space | null, { workspaceId: string; spaceId: string }>({
      async queryFn({ workspaceId, spaceId }) {
        try {
          const space = await getSpaceById(workspaceId, spaceId);
          return { data: space };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { spaceId }) => [{ type: "Space", id: spaceId }],
    }),

    getSpaceBySlug: build.query<Space | null, { workspaceId: string; slug: string }>({
      async queryFn({ workspaceId, slug }) {
        try {
          const space = await getSpaceBySlug(workspaceId, slug);
          return { data: space };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result) =>
        _result ? [{ type: "Space", id: _result.id }] : ["Space"],
    }),

    createSpace: build.mutation<
      Space,
      {
        workspaceId: string;
        data: { name: string; description?: string; iconUrl?: string; color?: string };
        creatorId: string;
      }
    >({
      async queryFn({ workspaceId, data, creatorId }) {
        try {
          const space = await createSpace(workspaceId, data, creatorId);
          return { data: space };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Space", id: workspaceId },
      ],
    }),

    updateSpace: build.mutation<
      Space | null,
      {
        workspaceId: string;
        spaceId: string;
        data: Partial<Pick<Space, "name" | "description" | "iconUrl" | "color" | "order">>;
      }
    >({
      async queryFn({ workspaceId, spaceId, data }) {
        try {
          const space = await updateSpace(workspaceId, spaceId, data);
          return { data: space };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId, spaceId }) => [
        { type: "Space", id: workspaceId },
        { type: "Space", id: spaceId },
      ],
    }),

    deleteSpace: build.mutation<boolean, { workspaceId: string; spaceId: string }>({
      async queryFn({ workspaceId, spaceId }) {
        try {
          const result = await deleteSpace(workspaceId, spaceId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Space", id: workspaceId },
        "List",
        "Task",
      ],
    }),

    // ========================================================================
    // List Endpoints
    // ========================================================================

    getLists: build.query<List[], { workspaceId: string; spaceId: string }>({
      async queryFn({ workspaceId, spaceId }) {
        try {
          const lists = await getLists(workspaceId, spaceId);
          return { data: lists };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { spaceId }) => [{ type: "List", id: spaceId }],
    }),

    getListById: build.query<
      List | null,
      { workspaceId: string; spaceId: string; listId: string }
    >({
      async queryFn({ workspaceId, spaceId, listId }) {
        try {
          const list = await getListById(workspaceId, spaceId, listId);
          return { data: list };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { listId }) => [{ type: "List", id: listId }],
    }),

    getListBySlug: build.query<
      List | null,
      { workspaceId: string; spaceId: string; slug: string }
    >({
      async queryFn({ workspaceId, spaceId, slug }) {
        try {
          const list = await getListBySlug(workspaceId, spaceId, slug);
          return { data: list };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result) =>
        _result ? [{ type: "List", id: _result.id }] : ["List"],
    }),

    createList: build.mutation<
      List,
      {
        workspaceId: string;
        spaceId: string;
        data: { name: string; description?: string; statuses?: KanbanStatus[] };
        creatorId: string;
      }
    >({
      async queryFn({ workspaceId, spaceId, data, creatorId }) {
        try {
          const list = await createList(workspaceId, spaceId, data, creatorId);
          return { data: list };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { spaceId }) => [{ type: "List", id: spaceId }],
    }),

    updateList: build.mutation<
      List | null,
      {
        workspaceId: string;
        spaceId: string;
        listId: string;
        data: Partial<Pick<List, "name" | "description" | "statuses" | "order">>;
      }
    >({
      async queryFn({ workspaceId, spaceId, listId, data }) {
        try {
          const list = await updateList(workspaceId, spaceId, listId, data);
          return { data: list };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { spaceId, listId }) => [
        { type: "List", id: spaceId },
        { type: "List", id: listId },
      ],
    }),

    deleteList: build.mutation<
      boolean,
      { workspaceId: string; spaceId: string; listId: string }
    >({
      async queryFn({ workspaceId, spaceId, listId }) {
        try {
          const result = await deleteList(workspaceId, spaceId, listId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { spaceId }) => [
        { type: "List", id: spaceId },
        "Task",
      ],
    }),

    // ========================================================================
    // Task Endpoints
    // ========================================================================

    getTasksByList: build.query<Task[], string>({
      async queryFn(listId) {
        try {
          const tasks = await getTasksByList(listId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, listId) => [{ type: "Task", id: listId }],
    }),

    getTasksByStatus: build.query<Task[], { listId: string; statusId: string }>({
      async queryFn({ listId, statusId }) {
        try {
          const tasks = await getTasksByStatus(listId, statusId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { listId, statusId }) => [
        { type: "Task", id: `${listId}-${statusId}` },
      ],
    }),

    getTaskById: build.query<Task | null, string>({
      async queryFn(taskId) {
        try {
          const task = await getTaskById(taskId);
          return { data: task };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, taskId) => [{ type: "Task", id: taskId }],
    }),

    getSubtasks: build.query<Task[], string>({
      async queryFn(parentTaskId) {
        try {
          const tasks = await getSubtasks(parentTaskId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, parentTaskId) => [
        { type: "Task", id: `subtasks-${parentTaskId}` },
      ],
    }),

    queryTasks: build.query<
      PaginatedTasksResponse,
      {
        filters: TaskQueryFilters;
        limit?: number;
        cursor?: string;
        orderBy?: "dueDate" | "createdAt" | "priority" | "orderInStatus";
        orderDirection?: "asc" | "desc";
      }
    >({
      async queryFn(request) {
        try {
          const result = await queryTasks(request);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: ["Task"],
    }),

    getTasksByListGrouped: build.query<TasksByStatus, { listId: string; statuses: KanbanStatus[] }>({
      async queryFn({ listId, statuses }) {
        try {
          const tasks = await getTasksByList(listId);
          const grouped: TasksByStatus = {};

          // Initialize all status columns
          statuses.forEach((status) => {
            grouped[status.id] = [];
          });

          // Group tasks by status
          tasks.forEach((task) => {
            if (grouped[task.statusId]) {
              grouped[task.statusId].push(task);
            } else {
              // If status doesn't exist, add to first column
              const firstStatus = statuses[0]?.id || "todo";
              if (!grouped[firstStatus]) grouped[firstStatus] = [];
              grouped[firstStatus].push(task);
            }
          });

          // Sort by order within each status
          Object.keys(grouped).forEach((statusId) => {
            grouped[statusId].sort((a, b) => a.orderInStatus - b.orderInStatus);
          });

          return { data: grouped };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { listId }) => [{ type: "Task", id: listId }],
    }),

    createTask: build.mutation<Task, { request: CreateTaskRequest; creatorId: string }>({
      async queryFn({ request, creatorId }) {
        try {
          const task = await createTask(request, creatorId);
          return { data: task };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { request }) => {
        const tags: Array<{ type: string; id?: string } | string> = [
          { type: "Task", id: request.listId },
          { type: "List", id: request.spaceId },
        ];
        if (request.goalId) {
          tags.push({ type: "Goal", id: request.goalId });
          tags.push({ type: "Task", id: `goal-${request.goalId}` });
        }
        if (request.keyResultId) {
          tags.push({ type: "KeyResult", id: request.keyResultId });
        }
        return tags as any;
      },
    }),

    updateTask: build.mutation<
      Task | null,
      { request: UpdateTaskRequest; updaterId: string; updaterDisplayName: string }
    >({
      async queryFn({ request, updaterId, updaterDisplayName }) {
        try {
          const task = await updateTask(request, updaterId, updaterDisplayName);
          return { data: task };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result) => {
        const tags: Array<{ type: string; id?: string } | string> = [
          "Task",
          "Goal",
          "KeyResult",
        ];
        return tags as any;
      },
    }),

    moveTask: build.mutation<Task | null, { request: MoveTaskRequest; userId: string }>({
      async queryFn({ request, userId }) {
        try {
          const task = await moveTask(request, userId);
          return { data: task };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: () => {
        return ["Task", "Goal", "KeyResult"] as any;
      },
    }),

    deleteTask: build.mutation<boolean, string>({
      async queryFn(taskId) {
        try {
          const result = await deleteTask(taskId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: ["Task", "List"],
    }),

    addTaskAssignee: build.mutation<
      Task | null,
      {
        taskId: string;
        assignee: TaskAssignee;
        assignerId: string;
        assignerDisplayName: string;
      }
    >({
      async queryFn({ taskId, assignee, assignerId, assignerDisplayName }) {
        try {
          const task = await addTaskAssignee(
            taskId,
            assignee,
            assignerId,
            assignerDisplayName
          );
          return { data: task };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [{ type: "Task", id: taskId }],
    }),

    removeTaskAssignee: build.mutation<
      Task | null,
      {
        taskId: string;
        userId: string;
        removerId: string;
        removerDisplayName: string;
      }
    >({
      async queryFn({ taskId, userId, removerId, removerDisplayName }) {
        try {
          const task = await removeTaskAssignee(
            taskId,
            userId,
            removerId,
            removerDisplayName
          );
          return { data: task };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [{ type: "Task", id: taskId }],
    }),

    reorderTasksInStatus: build.mutation<
      void,
      { listId: string; statusId: string; orderedTaskIds: string[] }
    >({
      async queryFn({ listId, statusId, orderedTaskIds }) {
        try {
          await reorderTasksInStatus(listId, statusId, orderedTaskIds);
          return { data: undefined };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { listId }) => [{ type: "Task", id: listId }],
    }),

    // ========================================================================
    // Comment Endpoints
    // ========================================================================

    getTaskComments: build.query<TaskComment[], string>({
      async queryFn(taskId) {
        try {
          const comments = await getTaskComments(taskId);
          return { data: comments };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, taskId) => [{ type: "Comment", id: taskId }],
    }),

    addTaskComment: build.mutation<
      TaskComment,
      {
        taskId: string;
        content: string;
        creatorId: string;
        creatorDisplayName?: string;
        mentions?: TaskComment["mentions"];
      }
    >({
      async queryFn({ taskId, content, creatorId, creatorDisplayName, mentions = [] }) {
        try {
          const comment = await addTaskComment(
            taskId,
            content,
            creatorId,
            mentions,
            creatorDisplayName || "User"
          );
          return { data: comment };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Comment", id: taskId },
        { type: "Task", id: taskId },
      ],
    }),

    updateTaskComment: build.mutation<
      TaskComment | null,
      { taskId: string; commentId: string; content: string }
    >({
      async queryFn({ taskId, commentId, content }) {
        try {
          const comment = await updateTaskComment(taskId, commentId, content);
          return { data: comment };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [{ type: "Comment", id: taskId }],
    }),

    // ========================================================================
    // Attachment Endpoints
    // ========================================================================

    getTaskAttachments: build.query<TaskAttachment[], string>({
      async queryFn(taskId) {
        try {
          const attachments = await getTaskAttachments(taskId);
          return { data: attachments };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, taskId) => [{ type: "Attachment", id: taskId }],
    }),

    addTaskAttachment: build.mutation<
      TaskAttachment,
      {
        taskId: string;
        attachment: Omit<TaskAttachment, "id" | "taskId" | "createdAt" | "createdBy">;
        creatorId: string;
      }
    >({
      async queryFn({ taskId, attachment, creatorId }) {
        try {
          const created = await addTaskAttachment(taskId, attachment, creatorId);
          return { data: created };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Attachment", id: taskId },
        { type: "Task", id: taskId },
      ],
    }),

    deleteTaskAttachment: build.mutation<
      boolean,
      { taskId: string; attachmentId: string }
    >({
      async queryFn({ taskId, attachmentId }) {
        try {
          const result = await deleteTaskAttachment(taskId, attachmentId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Attachment", id: taskId },
        { type: "Task", id: taskId },
      ],
    }),

    deleteTaskComment: build.mutation<boolean, { taskId: string; commentId: string }>({
      async queryFn({ taskId, commentId }) {
        try {
          const result = await deleteTaskComment(taskId, commentId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Comment", id: taskId },
        { type: "Task", id: taskId },
      ],
    }),

    // ========================================================================
    // Activity Endpoints
    // ========================================================================

    getTaskActivity: build.query<TaskActivity[], { taskId: string; limit?: number }>({
      async queryFn({ taskId, limit = 50 }) {
        try {
          const activity = await getTaskActivity(taskId, limit);
          return { data: activity };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { taskId }) => [{ type: "Activity", id: taskId }],
    }),

    // ========================================================================
    // Dashboard / Summary Endpoints
    // ========================================================================

    getOverdueTasks: build.query<Task[], string>({
      async queryFn(workspaceId) {
        try {
          const tasks = await getOverdueTasks(workspaceId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: ["Task"],
    }),

    getTasksDueToday: build.query<Task[], string>({
      async queryFn(workspaceId) {
        try {
          const tasks = await getTasksDueToday(workspaceId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: ["Task"],
    }),

    getMyTasks: build.query<Task[], { workspaceId: string; userId: string }>({
      async queryFn({ workspaceId, userId }) {
        try {
          const tasks = await getMyTasks(workspaceId, userId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: ["Task"],
    }),

    getTasksByGoal: build.query<Task[], { workspaceId: string; goalId: string }>({
      async queryFn({ workspaceId, goalId }) {
        try {
          const tasks = await getTasksByGoal(workspaceId, goalId);
          return { data: tasks };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { goalId }) => [
        { type: "Task", id: `goal-${goalId}` },
        "Task",
      ],
    }),
  }),
});

export const {
  // Workspace
  useGetWorkspacesQuery,
  useGetWorkspacesByUserQuery,
  useGetWorkspaceByIdQuery,
  useGetWorkspaceBySlugQuery,
  useLazyGetWorkspaceBySlugQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useAddWorkspaceMemberMutation,
  useCreateWorkspaceInviteMutation,
  useGetWorkspaceInviteQuery,
  useAcceptWorkspaceInviteMutation,
  useDeclineWorkspaceInviteMutation,
  useRemoveWorkspaceMemberMutation,
  useDeleteWorkspaceMutation,
  // Space
  useGetSpacesQuery,
  useGetSpaceByIdQuery,
  useGetSpaceBySlugQuery,
  useLazyGetSpaceBySlugQuery,
  useCreateSpaceMutation,
  useUpdateSpaceMutation,
  useDeleteSpaceMutation,
  // List
  useGetListsQuery,
  useLazyGetListsQuery,
  useGetListByIdQuery,
  useGetListBySlugQuery,
  useLazyGetListBySlugQuery,
  useCreateListMutation,
  useUpdateListMutation,
  useDeleteListMutation,
  // Task
  useGetTasksByListQuery,
  useLazyGetTasksByListQuery,
  useGetTasksByStatusQuery,
  useGetTaskByIdQuery,
  useLazyGetTaskByIdQuery,
  useGetSubtasksQuery,
  useQueryTasksQuery,
  useLazyQueryTasksQuery,
  useGetTasksByListGroupedQuery,
  useLazyGetTasksByListGroupedQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useMoveTaskMutation,
  useDeleteTaskMutation,
  useAddTaskAssigneeMutation,
  useRemoveTaskAssigneeMutation,
  useReorderTasksInStatusMutation,
  // Comment
  useGetTaskCommentsQuery,
  useAddTaskCommentMutation,
  useUpdateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  // Attachment
  useGetTaskAttachmentsQuery,
  useAddTaskAttachmentMutation,
  useDeleteTaskAttachmentMutation,
  // Activity
  useGetTaskActivityQuery,
  // Dashboard
  useGetOverdueTasksQuery,
  useGetTasksDueTodayQuery,
  useGetMyTasksQuery,
  // Goal-linked tasks
  useGetTasksByGoalQuery,
  useLazyGetTasksByGoalQuery,
} = kanbanApi;

export default kanbanApi;
